/**
 * view-concerns.ts — the SIX format-agnostic concerns of the shared a11y-view
 * substrate, as pure typed logic over the shared `ViewIR` / `ViewOp` shapes.
 *
 * Epic: docs/epics/closed/pptx-diff-view-260714/main.md (Phase 2a — extract-first).
 * Design: docs/design/pptx-diff-view-260714.md §5.1 + docs/design/docx-diff-view-260714.md §5.1.
 *
 * WHY. The shipped PDF editor (`web/static/editor/`) implements these six concerns
 * against PDF-specific types (Block/StructId/Card). Their LOGIC — box positioning,
 * badge numbering, the scroll window, linked-view highlight, the op reducer/inverse,
 * findings grouping — is format-agnostic (design §5.1). Extracting the pure logic
 * here lets PDF (via PdfViewAdapter), PPTX, and DOCX all mount ONE implementation
 * (rule #11 extract-on-the-second-site; A.6 fix-once-benefits-all). This module is
 * the substrate's brain; the DOM-mount glue (the actual overlay <div>s, the SR panel)
 * lands in Phase 3 when the first non-PDF view mounts — Phase 2a establishes the
 * TYPED SEAM the PDF adapter validates.
 *
 * The six concerns (each a pure function group below):
 *   1. Overlay boxes + reading-order badges — box→canvas math + badge numbering.
 *   2. Continuous multi-page scroll — the virtualized visible-window computation.
 *   3. Structure text pane + linked views — row ↔ item highlight resolution.
 *   4. Screen-reader POPOUT — the speakable transcript ordering (shared with #3 rows).
 *   5. Op / reducer / oplog — vocabulary-gated reduce + inverse over the closed set.
 *   6. Findings panel — grouping + per-item finding lookup.
 *
 * No DOM globals at module scope — import-safe in Node.js vitest without a jsdom shim.
 *
 * @module a11y-view/view-concerns
 */
import { normalizeSpeakableText } from "./sr-shared.js";
/**
 * Convert a normalized box (0..1) to a PERCENTAGE (0..100) rect for absolute
 * positioning over a rendered page image. Percentages — not CSS pixels — because the
 * `.slide-image` renders responsively (`width:100%`) at a size that differs from the
 * backend `canvas` dims; a percentage overlay scales WITH the displayed image, so it
 * can never exceed the PNG. The bbox already arrives normalized against the canvas
 * (design §R2 single-source; EMU/user-space/render normalized backend-side), so the
 * conversion is a pure ×100 and needs no canvas dims.
 */
export function placeBox(bbox) {
    return {
        left: bbox.x * 100,
        top: bbox.y * 100,
        width: bbox.w * 100,
        height: bbox.h * 100,
    };
}
/**
 * Produce the placed overlay boxes for one page, in reading-order-badge order.
 * Format-agnostic: the badge number is the item's global `readingOrderIndex`
 * (constraint C1 — numbering runs across the whole document, not per-page).
 */
export function placedBoxesForPage(page) {
    return page.items
        .slice()
        .sort((a, b) => a.readingOrderIndex - b.readingOrderIndex)
        .map((it) => {
        const r = placeBox(it.bbox);
        return {
            itemId: it.itemId,
            left: r.left,
            top: r.top,
            width: r.width,
            height: r.height,
            readingOrderIndex: it.readingOrderIndex,
            role: it.role,
            isBand: it.geometryConfidence === "band",
            ...(it.issue !== undefined ? { issue: it.issue } : {}),
        };
    });
}
// ─── Concern 2: continuous multi-page scroll (virtualized window) ─────────────
/**
 * The indices of pages that should currently be MOUNTED (rendered + overlays live)
 * given the scroll position — the format-agnostic core of the virtualized continuous
 * scroll (constraint C1). `overscan` mounts N pages beyond the viewport each way so
 * scrolling is smooth and lazy image fetches (design §G Q1) start before the page is
 * visible.
 *
 * @param firstVisible 0-based index of the topmost visible page.
 * @param lastVisible  0-based index of the bottommost visible page.
 * @param pageCount    total pages.
 * @param overscan     pages to keep mounted beyond the viewport (default 2).
 */
export function visiblePageWindow(firstVisible, lastVisible, pageCount, overscan = 2) {
    if (pageCount <= 0)
        return [];
    const lo = Math.max(0, Math.min(firstVisible, lastVisible) - overscan);
    const hi = Math.min(pageCount - 1, Math.max(firstVisible, lastVisible) + overscan);
    const out = [];
    for (let i = lo; i <= hi; i++)
        out.push(i);
    return out;
}
// ─── Concern 3 + 4: Structure pane + linked views + screen-reader order ───────
/**
 * Find the Structure-pane / screen-reader row for a selected overlay item — the
 * linked-view highlight resolution (box → row). Returns the row index (for scroll +
 * ARIA focus) or -1.
 */
export function rowIndexForItem(rows, itemId) {
    return rows.findIndex((r) => r.itemId === itemId);
}
/**
 * The reverse linked-view direction (row → box): the itemId a Structure-pane row
 * points at, so selecting a row highlights + scrolls to its overlay box.
 */
export function itemIdForRow(rows, rowIndex) {
    const r = rows[rowIndex];
    return r ? r.itemId : null;
}
/**
 * The speakable-order rows for the screen-reader POPOUT. Reading order IS the row
 * order (the backend emits rows in reading order — PDF geometric, PPTX shape-tree,
 * DOCX body-flow), so the SR transcript order equals `ir.rows`. This is the home the
 * docstring long promised for the speakability trim (design §4.4): each row's text is
 * whitespace-normalized via the SHARED `normalizeSpeakableText` (the SAME helper the
 * classic pdf.js editor's SR path consumes — rule #11), collapsing the embedded
 * newlines the O365 editor-IR CLIs emit so a paragraph reads as one utterance instead
 * of cramped multi-line runs (config-drift RCA Defect 3). Pure — returns a new array.
 */
export function screenReaderOrder(ir) {
    return ir.rows.map((r) => ({ ...r, text: normalizeSpeakableText(r.text) }));
}
/**
 * Resolve an item's current box (alt / decorative / role state) by its opaque
 * `itemId`, scanning all pages. Returns null when no item matches. Pure. Used by the
 * editable mount to POPULATE the inspector on selection — the inspector reads the
 * item's live alt/decorative before offering an edit, so it never shows stale state.
 */
export function itemById(ir, itemId) {
    for (const p of ir.pages) {
        for (const it of p.items) {
            if (it.itemId === itemId)
                return it;
        }
    }
    return null;
}
// ─── Concern 5: op / reducer / oplog (vocabulary-gated) ───────────────────────
/**
 * Thrown when a view op is dispatched that the format's adapter did not declare in
 * its `opVocabulary` (constraint C6 closure enforcement). Distinct class so callers
 * can surface it as "this edit is not permitted for this format" vs a bug.
 */
export class ViewOpNotPermittedError extends Error {
    opKind;
    constructor(opKind, format) {
        super(`View op "${opKind}" is not in the ${format ?? "format"}'s op vocabulary ` +
            `(constraint C6 closed op set)`);
        this.name = "ViewOpNotPermittedError";
        this.opKind = opKind;
    }
}
/**
 * Gate a view op against a format's closed vocabulary. Throws
 * `ViewOpNotPermittedError` if the op's kind is not declared. This is the structural
 * enforcement of C6 ("a11y-only edits") and the DOCX "no reorder" rule (→ A.5): the
 * substrate calls this before dispatching, so a format cannot apply an op it did not
 * declare — the closure is checked, not merely conventional.
 */
export function assertOpPermitted(op, vocabulary, format) {
    if (!vocabulary.has(op.kind)) {
        throw new ViewOpNotPermittedError(op.kind, format);
    }
}
/**
 * The inverse of a view op, for undo/redo. Every op in the closed set is invertible
 * by swapping before/after (no create/delete ops exist in the a11y-only vocabulary,
 * so there is no asymmetric case as in the PDF-only op set). Pure.
 */
export function inverseViewOp(op) {
    switch (op.kind) {
        case "set-alt-text":
            return { ...op, before: op.after, after: op.before };
        case "set-decorative":
            return { ...op, before: op.after, after: op.before };
        case "set-lang":
            return { ...op, before: op.after, after: op.before };
        case "set-title":
            return { ...op, before: op.after, after: op.before };
        case "set-long-desc":
            return { ...op, before: op.after, after: op.before };
        case "reorder-reading":
            return { ...op, before: op.after, after: op.before };
    }
}
export function applyViewOp(ir, op) {
    const dirtyPages = new Set();
    // Doc-level ops: title / lang. Page-index sentinel −1 = doc-level.
    if (op.kind === "set-title") {
        dirtyPages.add(-1);
        return { ir: { ...ir, title: op.after }, dirtyPages };
    }
    if (op.kind === "set-lang") {
        dirtyPages.add(-1);
        return { ir: { ...ir, language: op.after }, dirtyPages };
    }
    if (op.kind === "reorder-reading") {
        const pages = ir.pages.map((p) => {
            if (p.pageIndex !== op.pageIndex)
                return p;
            dirtyPages.add(p.pageIndex);
            // Re-number reading indices to match op.after's item order, preserving
            // each item's existing badge base (global numbering is recomputed by the
            // host after a reorder; here we only re-key intra-page order).
            const orderById = new Map();
            op.after.forEach((id, i) => orderById.set(id, i));
            const items = p.items
                .slice()
                .sort((a, b) => {
                const ia = orderById.has(a.itemId) ? orderById.get(a.itemId) : Number.MAX_SAFE_INTEGER;
                const ib = orderById.has(b.itemId) ? orderById.get(b.itemId) : Number.MAX_SAFE_INTEGER;
                return ia - ib;
            });
            return { ...p, items };
        });
        return { ir: { ...ir, pages }, dirtyPages };
    }
    // Item-level ops: set-alt-text / set-decorative / set-long-desc.
    const pages = ir.pages.map((p) => {
        let touched = false;
        const items = p.items.map((it) => {
            if (it.itemId !== op.itemId)
                return it;
            touched = true;
            if (op.kind === "set-alt-text")
                return { ...it, alt: op.after };
            if (op.kind === "set-decorative")
                return { ...it, decorative: op.after };
            // set-long-desc has no dedicated ViewItemBox field in the founding shape;
            // it is a no-op on the box (the long-desc lives in the format's own IR and
            // is persisted by the Phase-4 edit route). We still mark the page dirty so
            // the findings/row re-render picks up the change.
            return it;
        });
        if (!touched)
            return p;
        dirtyPages.add(p.pageIndex);
        return { ...p, items };
    });
    return { ir: { ...ir, pages }, dirtyPages };
}
/** Group findings by severity (errors first, then warnings, then info). */
export function groupFindings(findings) {
    const out = { error: [], warning: [], info: [] };
    for (const f of findings)
        out[f.severity].push(f);
    return out;
}
/** All findings attached to one item. */
export function findingsForItem(findings, itemId) {
    return findings.filter((f) => f.itemId === itemId);
}
/**
 * The ORDERED set of itemIds that have at least one finding attached — the "things
 * to fix" targets, in READING ORDER (the order the SR announces them / the badges
 * number them). Drives both the "Next thing to fix" navigator (cycle through this
 * list) and the "only show things to fix" filter (show only these overlays/cards).
 * Doc-level findings (no `itemId`) are not per-item targets, so they are excluded
 * here — the nav walks the per-item fixables. Deduplicated; first occurrence wins.
 */
export function fixableItemIds(ir) {
    const withFindings = new Set();
    for (const f of ir.findings) {
        if (f.itemId !== undefined)
            withFindings.add(f.itemId);
    }
    // Walk items in reading order (global index) so the nav visits them top-to-bottom.
    const ordered = [];
    const seen = new Set();
    const allItems = ir.pages
        .flatMap((p) => p.items)
        .slice()
        .sort((a, b) => a.readingOrderIndex - b.readingOrderIndex);
    for (const it of allItems) {
        if (withFindings.has(it.itemId) && !seen.has(it.itemId)) {
            ordered.push(it.itemId);
            seen.add(it.itemId);
        }
    }
    return ordered;
}
/**
 * Map each overlay item's opaque `itemId` → the 0-based page it lives on. The
 * format-agnostic itemId→page join the XLSX paged mode (view-adapter `displayMode`)
 * uses to switch the active sheet when the "Next thing to fix" navigator lands on an
 * item that lives on a non-active sheet (Phase-1b R1 ruling 2). Pure; drawings only
 * (Structure-pane cell/header rows carry their sheet on `TextViewRow.pageIndex`).
 */
export function pageIndexByItem(ir) {
    const out = new Map();
    for (const p of ir.pages) {
        for (const it of p.items)
            out.set(it.itemId, p.pageIndex);
    }
    return out;
}
/**
 * Count of FIXABLE items per page — the per-sheet "things to fix" badge model for the
 * XLSX sheet-switcher tabs (Phase-1b R1 ruling 3: the cross-sheet total stays visible
 * as per-tab badges while the fix-nav count scopes to the active sheet). Fixables are
 * the reading-ordered items carrying at least one finding (`fixableItemIds`); a fixable
 * with no owning page (a doc-level target) is skipped. Pure.
 */
export function fixableCountByPage(ir) {
    const byItem = pageIndexByItem(ir);
    const out = new Map();
    for (const id of fixableItemIds(ir)) {
        const pg = byItem.get(id);
        if (pg === undefined)
            continue;
        out.set(pg, (out.get(pg) ?? 0) + 1);
    }
    return out;
}
/**
 * The NEXT fixable itemId to jump to from the currently-selected item — the "Next
 * thing to fix" navigator model. Cycles: if `currentItemId` is a fixable, returns
 * the one after it (wrapping to the first); if it is null / not a fixable, returns
 * the first fixable. Returns null when there are no fixables. Pure — the caller
 * scrolls to + selects the returned id, which PRESERVES CONTEXT (the surrounding
 * document/structure stay mounted; only the selection/scroll moves).
 */
export function nextFixableItem(ir, currentItemId) {
    const fixables = fixableItemIds(ir);
    if (fixables.length === 0)
        return null;
    if (currentItemId === null)
        return fixables[0];
    const idx = fixables.indexOf(currentItemId);
    if (idx < 0)
        return fixables[0];
    return fixables[(idx + 1) % fixables.length];
}
